import React, { useState } from 'react';
import { PencilIcon, TrashIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import ReactionDisplay from './ReactionDisplay';
import ReactionPicker from './ReactionPicker';
import CommentComposer from './CommentComposer';
import ConfirmationModal from '../common/ConfirmationModal';
import { renderContentWithMentions } from '../../utils/mentionUtils';

const CommentItem = ({
  comment,
  currentUser,
  onEdit,
  onDelete,
  onReply,
  onReaction,
  depth = 0
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isOwner = currentUser?.id === comment.user_id;
  const author = comment.profiles || {};
  const authorName = `${author.first_name || ''} ${author.last_name || ''}`.trim() || author.email || 'Unknown';
  const isDeleted = comment.is_deleted || comment.content === '[deleted]';

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleEditSubmit = async (data) => {
    await onEdit(comment.id, data);
    setIsEditing(false);
  };

  const handleReplySubmit = async (data) => {
    await onReply(comment.id, data);
    setIsReplying(false);
  };

  const handleDelete = () => {
    onDelete(comment.id);
  };

  return (
    <div className="mt-4" style={{ marginLeft: `${depth * 3}rem` }}>
      <div className="flex space-x-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {author.avatar_url ? (
            <img
              src={author.avatar_url}
              alt={authorName}
              className="h-8 w-8 rounded-full"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-medium text-sm">
                {authorName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Comment Content */}
        <div className="flex-1 min-w-0">
          {isDeleted ? (
            /* Deleted Comment Display */
            <div className="bg-secondary-100 rounded-lg px-4 py-3 border border-secondary-200">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-secondary-400 italic">
                  [Comment deleted]
                </span>
                <span className="text-xs text-secondary-400">
                  {formatTimestamp(comment.deleted_at || comment.created_at)}
                </span>
              </div>
            </div>
          ) : (
            /* Normal Comment Display */
            <div className="bg-secondary-50 rounded-lg px-4 py-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-secondary-900">
                    {authorName}
                  </span>
                  <span className="text-xs text-secondary-500">
                    {formatTimestamp(comment.created_at)}
                  </span>
                  {comment.edited_at && (
                    <span className="text-xs text-secondary-400 italic">
                      (edited)
                    </span>
                  )}
                  {comment.is_internal && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Internal
                    </span>
                  )}
                </div>

                {/* Actions (for comment owner) */}
                {isOwner && !isEditing && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-1 text-secondary-400 hover:text-secondary-600"
                      title="Edit comment"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="p-1 text-secondary-400 hover:text-red-600"
                      title="Delete comment"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Content */}
              {isEditing ? (
                <CommentComposer
                  initialContent={comment.content}
                  initialAttachments={comment.attachments}
                  initialIsInternal={comment.is_internal}
                  onSubmit={handleEditSubmit}
                  onCancel={() => setIsEditing(false)}
                  submitLabel="Save"
                />
              ) : (
                <>
                  <div className="text-sm text-secondary-700 whitespace-pre-wrap">
                    {renderContentWithMentions(comment.content)}
                  </div>

                {/* Attachments */}
                {comment.attachments && comment.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {comment.attachments.map((attachment, idx) => (
                      <a
                        key={idx}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        {attachment.filename || 'Attachment'}
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          )}

          {/* Reactions and Reply - Only for non-deleted comments */}
          {!isDeleted && !isEditing && (
            <div className="flex items-center space-x-4 mt-2 ml-1">
              {/* Reactions Display */}
              <ReactionDisplay
                reactions={comment.reactions || {}}
                onReactionClick={(emoji) => onReaction(comment.id, emoji)}
                currentUserId={currentUser?.id}
              />

              {/* Add Reaction Button */}
              <div className="relative">
                <button
                  onClick={() => setShowReactionPicker(!showReactionPicker)}
                  className="text-xs text-secondary-500 hover:text-secondary-700 font-medium"
                >
                  Add reaction
                </button>
                {showReactionPicker && (
                  <ReactionPicker
                    onSelect={(emoji) => {
                      onReaction(comment.id, emoji);
                      setShowReactionPicker(false);
                    }}
                    onClose={() => setShowReactionPicker(false)}
                  />
                )}
              </div>

              {/* Reply Button */}
              <button
                onClick={() => setIsReplying(!isReplying)}
                className="text-xs text-secondary-500 hover:text-secondary-700 font-medium flex items-center"
              >
                <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                Reply
              </button>
            </div>
          )}

          {/* Reply Composer */}
          {isReplying && (
            <div className="mt-3">
              <CommentComposer
                onSubmit={handleReplySubmit}
                onCancel={() => setIsReplying(false)}
                placeholder="Write a reply..."
                submitLabel="Reply"
              />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default CommentItem;
